import { Heart, Target, Eye, Users, Award, BookOpen, Quote } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';
import { teamMembers } from '../mock';

export default function About() {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-orange-50 via-amber-50 to-orange-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center bg-orange-100 text-orange-800 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Heart className="h-4 w-4 mr-2" />
              Our Story
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              About The Blanket Project
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              More than just a charitable organization, we are a beacon of hope striving to make 
              a tangible difference in the lives of those who need it most.
            </p>
          </div>
        </div>
      </section>

      {/* Scripture Foundation */}
      <section className="py-16 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-8 rounded-2xl border border-orange-100">
            <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-6 text-center">
              Our Foundation in Scripture
            </h2>
            <Quote className="h-8 w-8 text-orange-500 mx-auto mb-4" />
            <blockquote className="text-lg text-gray-700 italic text-center leading-relaxed mb-4">
              "At the heart of our mission is the scripture from Matthew 25:35-36. These verses inspire 
              us to see every act of service as a reflection of our commitment to Christ's call for 
              unconditional love and hospitality."
            </blockquote>
          </div>
        </div>
      </section>

      {/* Mission, Vision, Values */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Our Purpose & Vision
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Guided by faith and inspired by Christ's love, we work to transform lives and communities
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8 text-center">
                <div className="bg-gradient-to-br from-orange-100 to-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Target className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Mission</h3>
                <p className="text-gray-600 leading-relaxed">
                  To uplift and empower underprivileged and underserved communities by providing essential resources, 
                  fostering connections, and igniting a spirit of unity and love.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8 text-center">
                <div className="bg-gradient-to-br from-orange-100 to-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Eye className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Vision</h3>
                <p className="text-gray-600 leading-relaxed">
                  A world where warmth is a right, not a privilege, and where every individual 
                  experiences the compassion and care they deserve through Christ's love.
                </p>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg bg-white">
              <CardContent className="p-8 text-center">
                <div className="bg-gradient-to-br from-orange-100 to-amber-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="h-8 w-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Our Values</h3>
                <p className="text-gray-600 leading-relaxed">
                  Compassion, dignity, faith, and service guide every action we take. We believe 
                  every person deserves love, respect, and support regardless of their circumstances.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How We Serve */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              How We Serve
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Our three-pillar approach to community service and transformation
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="bg-gradient-to-br from-orange-100 to-amber-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Serve with Compassion</h3>
              <p className="text-gray-600 leading-relaxed">
                We provide tangible support—from distributing blankets, clothing, and hygiene products 
                to offering access to food, health, and wellness resources.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-br from-orange-100 to-amber-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Empower Communities</h3>
              <p className="text-gray-600 leading-relaxed">
                We partner with local organizations and churches to create educational and vocational 
                opportunities, helping individuals gain skills and self-reliance.
              </p>
            </div>

            <div className="text-center">
              <div className="bg-gradient-to-br from-orange-100 to-amber-100 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6">
                <BookOpen className="h-10 w-10 text-orange-600" />
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-4">Inspire Through Faith</h3>
              <p className="text-gray-600 leading-relaxed">
                Rooted in our Christian values, we offer spiritual guidance and support, inviting 
                everyone to experience the healing and transformative power of God's love.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Meet Our Founders
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Dedicated leaders committed to serving God's kingdom through biblical teaching and community outreach
            </p>
          </div>
          
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 max-w-5xl mx-auto">
            {teamMembers.map((member) => (
              <Card key={member.id} className="border-0 shadow-lg bg-white overflow-hidden">
                <CardContent className="p-0">
                  <div className="md:flex">
                    <div className="md:w-1/3">
                      <div className="aspect-square md:aspect-[3/4] w-full">
                        <img 
                          src={member.image} 
                          alt={member.name}
                          className="w-full h-full object-cover"
                        />
                      </div>
                    </div>
                    <div className="md:w-2/3 p-8">
                      <h3 className="text-2xl font-bold text-gray-900 mb-2">{member.name}</h3>
                      <p className="text-orange-600 font-semibold mb-4">{member.role}</p>
                      <p className="text-gray-600 mb-6 leading-relaxed text-sm">{member.bio}</p>
                      
                      <div>
                        <h4 className="font-semibold text-gray-900 mb-2 flex items-center">
                          <Award className="h-4 w-4 mr-2 text-orange-600" />
                          Qualifications & Roles
                        </h4>
                        <ul className="text-sm text-gray-600 space-y-1">
                          {member.qualifications.map((qual, index) => (
                            <li key={index} className="flex items-start">
                              <span className="text-orange-600 mr-2">•</span>
                              {qual}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
            Be Part of Our Story
          </h2>
          <p className="text-lg text-gray-600 mb-8 leading-relaxed">
            Together, we can build a stronger, more caring community where every act of kindness 
            creates lasting transformation through God's love.
          </p>
          <div className="bg-white p-8 rounded-2xl shadow-lg border border-orange-100">
            <Quote className="h-8 w-8 text-orange-500 mx-auto mb-4" />
            <p className="text-lg text-gray-700 italic leading-relaxed">
              "Our mission is to provide complete ministry services including biblical teaching, fellowship, 
              discipleship, prayer support, and community outreach - serving believers with authentic 
              Christian community rooted in God's Word."
            </p>
            <p className="text-orange-600 font-semibold mt-4">- Sean & Rhoda Talluri</p>
          </div>
        </div>
      </section>
    </div>
  );
}